"use client";

import React, { useState } from "react";
import { ArrowUpDown, LayoutGrid, Search, Table } from "lucide-react";
import {
  MobileFilterBar,
  type MobileFilterBarAction,
} from "modules/common/components/MobileFilterBar";
import { Modal } from "modules/common/components/Modal/Modal";
import { MobileSearchGroup } from "modules/common/components/mainbar/MobileSearchGroup";
import {
  SortOptionsRadioGroup,
  SortDirectionRadioGroup,
} from "modules/common/components/mainbar/MobileSortControls";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { TRADE_ITEM_SORT_OPTIONS_MOBILE } from "modules/common/constants/tradeItemSortOptions";
import { UNIFIED_VIEWS } from "modules/common/model/viewTypes";
import { ItemSortOptionsEnum } from "modules/tradeLists/model/tradeList";
import { ShortSet } from "modules/common/model/models";

interface TradeItemsMobileFilterBarProps {
  // Core (required)
  query: string;
  sort: ItemSortOptionsEnum;
  onQueryChange: (query: string) => void;
  onSortChange: (sort: ItemSortOptionsEnum) => void;

  // Set filter (optional, for MTG)
  setCode?: string | null;
  sets?: ShortSet[];
  onSetCodeChange?: (setCode: string | null) => void;
  showSetFilter?: boolean;
  isLoadingUser?: boolean;

  // Sort direction (optional)
  order?: SortOrderEnum;
  onOrderChange?: (order: SortOrderEnum) => void;

  // View toggle (optional)
  viewIndex?: number;
  onViewChange?: (index: number) => void;

  // Extra action chips (optional)
  extraActions?: MobileFilterBarAction[];

  // Customization (optional)
  hideSearch?: boolean;
  hideSort?: boolean;
  searchPlaceholder?: string;
  className?: string;
}

export function TradeItemsMobileFilterBar({
  query,
  sort,
  onQueryChange,
  onSortChange,
  order,
  onOrderChange,
  setCode,
  sets = [],
  onSetCodeChange,
  showSetFilter = false,
  isLoadingUser = false,
  viewIndex,
  onViewChange,
  extraActions,
  hideSearch = false,
  hideSort = false,
  searchPlaceholder = "Search for card name...",
  className,
}: TradeItemsMobileFilterBarProps) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Local state for sort modal (synced when modal opens)
  const [localSort, setLocalSort] = useState(sort);
  const [localOrder, setLocalOrder] = useState(
    order ?? SortOrderEnum.Descending
  );

  // Local state for search input (synced from props on modal open)
  const [searchValue, setSearchValue] = useState(query);
  const [selectedSetCode, setSelectedSetCode] = useState<string | null>(
    setCode || null
  );

  const handleSearchCommit = () => {
    onQueryChange(searchValue);
    if (onSetCodeChange) {
      onSetCodeChange(selectedSetCode);
    }
    setIsSearchOpen(false);
  };

  const handleSearchClear = () => {
    setSearchValue("");
    setSelectedSetCode(null);
    onQueryChange("");
    if (onSetCodeChange) {
      onSetCodeChange(null);
    }
    setIsSearchOpen(false);
  };

  // Count active search/filter criteria for badge
  const activeSearchCount = [query, setCode].filter(Boolean).length;

  // Determine modal title based on whether set filter is shown
  const searchModalTitle =
    showSetFilter && !isLoadingUser ? "Search & Filter" : "Search";

  const mobileActions: MobileFilterBarAction[] = [];

  if (!hideSearch) {
    mobileActions.push({
      icon: <Search size={16} />,
      label: "Search",
      onClick: () => {
        setSearchValue(query);
        setSelectedSetCode(setCode || null);
        setIsSearchOpen(true);
      },
      active: activeSearchCount > 0,
      badge: activeSearchCount > 0 ? activeSearchCount : undefined,
    });
  }

  if (!hideSort) {
    mobileActions.push({
      icon: <ArrowUpDown size={16} />,
      label: "Sort",
      onClick: () => {
        setLocalSort(sort);
        setLocalOrder(order ?? SortOrderEnum.Descending);
        setIsSortOpen(true);
      },
    });
  }

  if (extraActions) {
    mobileActions.push(...extraActions);
  }

  if (viewIndex !== undefined && onViewChange) {
    mobileActions.push({
      icon: viewIndex === 0 ? <Table size={16} /> : <LayoutGrid size={16} />,
      label: UNIFIED_VIEWS[viewIndex]?.name ?? "Table",
      onClick: () => onViewChange(viewIndex === 0 ? 1 : 0),
    });
  }

  const allActions = mobileActions;

  return (
    <>
      <MobileFilterBar actions={allActions} className={className} />

      {/* Mobile: Search Modal */}
      <Modal
        open={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        title={searchModalTitle}
        height="auto"
        maxWidth="lg"
      >
        <div className="flex flex-col gap-6 py-2">
          <MobileSearchGroup
            value={searchValue}
            onChange={setSearchValue}
            onClear={() => setSearchValue("")}
            placeholder={searchPlaceholder}
          />

          {/* Set filter (conditional for MTG users) - native select for mobile */}
          {!isLoadingUser && showSetFilter && (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="mobile-set-filter"
                className="text-base-content text-sm font-medium"
              >
                Filter by Set
              </label>
              <select
                id="mobile-set-filter"
                className="select select-bordered w-full"
                value={selectedSetCode || ""}
                onChange={(e) => {
                  setSelectedSetCode(e.target.value || null);
                }}
              >
                <option value="">All Sets</option>
                {sets.map((set) => (
                  <option key={set.code} value={set.code}>
                    {set.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2">
            <button
              className="btn btn-ghost flex-1"
              onClick={handleSearchClear}
            >
              Clear
            </button>
            <button
              className="btn btn-primary flex-1"
              onClick={handleSearchCommit}
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>

      {/* Mobile: Sort Modal */}
      <Modal
        open={isSortOpen}
        onClose={() => setIsSortOpen(false)}
        title="Sort"
        height="auto"
        maxWidth="lg"
      >
        <div className="flex flex-col gap-6 py-2">
          <SortOptionsRadioGroup
            options={TRADE_ITEM_SORT_OPTIONS_MOBILE}
            currentSort={localSort}
            onSortChange={setLocalSort}
            label=""
          />
          {onOrderChange && (
            <SortDirectionRadioGroup
              currentOrder={localOrder}
              onOrderChange={setLocalOrder}
            />
          )}
          <div className="flex gap-2">
            <button
              className="btn btn-ghost flex-1"
              onClick={() => setIsSortOpen(false)}
            >
              Close
            </button>
            <button
              className="btn btn-primary flex-1"
              onClick={() => {
                onSortChange(localSort);
                if (onOrderChange) {
                  onOrderChange(localOrder);
                }
                setIsSortOpen(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
